import { format } from 'date-fns'
import EncryptionSDP from '@helpers/encrypt'
import { fetchEncryptedSDP } from '@services/finance'
import { getFromBrowserStorage } from './storage'
import { getCurrentLocation } from './geo-location'
import { getCartTotal, getCart, getStoreCartTotal } from './cart'
import { getRemainingTotal, getOrder } from './checkout/global'
// eslint-disable-next-line import/no-unresolved
import financePlanFeed from '../generators/data/finance-plan-feed.json'

const descendingPriority = (a, b) => a.priority - b.priority
const hasDownPayment = plan => plan.downPaymentRequired
const hasNoDownPayment = plan => !plan.downPaymentRequired

const getDropshipTotal = order => {
  let dropshipTotal = 0
  const lineItems = order?.lineItems
  if (lineItems) {
    const dropshipItems = lineItems.filter(item => item.deliveryType === 'O')
    dropshipTotal = dropshipItems.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0)
  }
  return dropshipTotal
}

const getPrimaryPlan = (plans, total) => {
  let primaryPlan = {}
  if (plans.length > 1) {
    for (let i = 0; i < plans.length; i++) {
      if (
        JSON.stringify(primaryPlan) === '{}' ||
        plans[i].threshold > primaryPlan.threshold ||
        plans[i].dropshipThreshold > primaryPlan.dropshipThreshold ||
        (plans[i].serviceFinanceCode === '360' && total < primaryPlan.threshold)
      ) {
        primaryPlan = plans[i]
      }
    }
  } else {
    // eslint-disable-next-line prefer-destructuring
    primaryPlan = plans[0]
  }
  return primaryPlan
}

export const getFinancingPriority = (financePlans, total, noThreshold, singleProductOnly = false) => {
  const order = getFromBrowserStorage('session', 'order')
  const dropshipTotal = getDropshipTotal(order)
  let modifiedTotal = total
  if (order && order.promotions && order.promotions.totalSavings && !singleProductOnly) {
    modifiedTotal -= order.promotions.totalSavings
  }
  const newFinancePlans = []

  const financePlanCodes = Array.from(
    financePlans.reduce((set, financePlan) => {
      set.add(financePlan.financeCode)
      return set
    }, new Set()),
  )

  for (let i = 0, n = financePlanCodes.length; i < n; i++) {
    let priority = 0
    let index = 0
    const plans = financePlans.filter(plan => plan.financeCode === financePlanCodes[i])
    for (let x = 0; x < plans.length; x++) {
      const planPriority = parseInt(plans[x].priority)
      if (priority < planPriority) {
        priority = planPriority
        index = x
      }
    }
    if (
      (noThreshold ||
        (modifiedTotal || order?.total || 0) >= plans[index].threshold ||
        (dropshipTotal >= plans[index].dropshipThreshold && plans[index].dropshipThreshold > 0)) &&
      newFinancePlans.filter(plan => plan.financeCode === plans[index].financeCode).length < 1
    ) {
      if (plans[index].downPaymentRequired) {
        newFinancePlans.unshift(plans[index])
      } else if (!plans[index].downPaymentRequired) {
        newFinancePlans.push(plans[index])
      }
    }
  }

  const downPaymentPlans = newFinancePlans.filter(hasDownPayment).sort(descendingPriority)
  const normalPlans = newFinancePlans.filter(hasNoDownPayment).sort(descendingPriority)
  const primaryDownPayment = getPrimaryPlan(downPaymentPlans, modifiedTotal)
  const primaryNormal = getPrimaryPlan(normalPlans)
  const genesisPlan = newFinancePlans.filter(plan => plan.financeCode === 'GENESIS')[0]
  return [primaryDownPayment, primaryNormal, genesisPlan]
}

/**
 * @returns {Array} finance plans for current region of user
 */
export const getFinancePlansFromRegion = () => {
  let financePlans = []
  if (financePlanFeed) {
    const loc = getCurrentLocation()
    if (loc) {
      financePlans = financePlanFeed[`${loc.region.toLowerCase()}${loc.price_zone}`]
        ? financePlanFeed[`${loc.region.toLowerCase()}${loc.price_zone}`]
        : financePlanFeed[`${loc.region.toLowerCase()}0`] || financePlanFeed.fl0
    } else {
      financePlans = financePlanFeed.fl0
    }
  }
  return financePlans
}

export const getFinancePlans = (
  isCheckout = false,
  price = 0,
  cart = false,
  noThreshold = false,
  singleProductOnly = false,
) => {
  let realFinancePlans = []
  const financePlans = getFinancePlansFromRegion()
  if (!isCheckout) return financePlans

  const order = getOrder()
  if (financePlans?.length > 0) {
    realFinancePlans = financePlans
    if (realFinancePlans && realFinancePlans?.length > 0) {
      const cartTotal = getCartTotal(getCart())
      const storeCartTotal = getStoreCartTotal(order?.lineItems?.filter(lineItem => lineItem.isStoreSku))
      const orderValues = getRemainingTotal(false, cartTotal + storeCartTotal, false, false)
      const totalForFin = singleProductOnly ? price : orderValues.total + price
      realFinancePlans = getFinancingPriority(
        financePlans,
        cart ? orderValues.total : totalForFin,
        noThreshold,
        singleProductOnly,
      )
    }
  }
  return realFinancePlans
}

export const getPromoMessage = (price, cart, singleProductOnly = false, isCheckout = false) => {
  const realFinancePlans = getFinancePlans(isCheckout, price, cart, true, singleProductOnly)
  const finForCalc = realFinancePlans.filter(plan => plan && plan.downPaymentRequired)?.[0]
  if (finForCalc) {
    const { promoMessage } = finForCalc || {}
    return promoMessage?.childMarkdownRemark?.html || null
  }
  return ''
}

/**
 *
 * @param {*} price product price
 * @param {*} deliveryType product delivery type
 * @returns {Object}
 * @property {number} financeAmount Finance Amount to pay each month
 * @property {bool} showFinance whether to showFinance or not
 */
export const MIN_FINANCE_AMOUNT = 5
export const productFinancing = (price, deliveryType, cart = false, singleProductOnly = false, sku, promoFinancing) => {
  const { changeInTotalCost, changeInTotalMonths } = promoFinancing ?? {}
  let finance = { financeAmount: 0, showFinance: false, financeNumMonths: 0 }
  const isCheckout = false
  if (sku === '83333333') {
    return finance
  }
  const realFinancePlans = getFinancePlans(isCheckout, price, cart, true, singleProductOnly)

  if (realFinancePlans?.length > 0) {
    const priorityZeroPlan = realFinancePlans.find(plan => plan?.priority === 0)
    if (priorityZeroPlan) {
      const thresholdAmount = priorityZeroPlan.threshold || 0
      const priceExceedsThreshold =
        price >= thresholdAmount ||
        (deliveryType === 'O' && priorityZeroPlan.dropshipThreshold > 0 && price >= priorityZeroPlan.dropshipThreshold)
      if (!priceExceedsThreshold) {
        return finance
      }
    }
    const finForCalc = realFinancePlans.filter(plan => plan && plan.downPaymentRequired)?.[0]
    if (finForCalc) {
      const { promotionalPrice, promotionalMonths, promoMessage } = finForCalc
      let promotionalFinanceAmount = 0
      let financeAmount = 0
      let financeNumMonths = finForCalc?.numberOfMonths || 0
      let totalCost = price
      const promoMessageHTML = promoMessage?.childMarkdownRemark?.html || null

      if (finForCalc?.numberOfMonths > 0) {
        if (promotionalPrice && promotionalMonths) {
          promotionalFinanceAmount = promotionalPrice * promotionalMonths
          financeNumMonths = finForCalc?.numberOfMonths - promotionalMonths
        }

        if (
          changeInTotalCost &&
          typeof changeInTotalCost === 'number' &&
          totalCost + changeInTotalCost >= 0 &&
          changeInTotalMonths &&
          typeof changeInTotalMonths === 'number' &&
          financeNumMonths + changeInTotalMonths >= 0
        ) {
          totalCost += changeInTotalCost ?? 0
          financeNumMonths += changeInTotalMonths ?? 0
        }
        let totalMultipler = 1

        if (financeNumMonths > 17) {
          totalMultipler = 1.02
        }

        financeAmount = Math.ceil(
          ((totalCost - promotionalFinanceAmount) * totalMultipler) / parseInt(financeNumMonths),
        )
      }
      finance = {
        financeAmount,
        financeNumMonths,
        promotionalPrice,
        promotionalMonths,
        promoMessageHTML,
        showFinance: financeAmount >= MIN_FINANCE_AMOUNT,
      }
    }
  }
  return finance
}

export const getFinanceMarketingMessageData = () => {
  const financePlans = getFinancePlans()?.filter?.(plan => !!plan) || []
  if (financePlans && financePlans.length > 0) {
    const downPayment = financePlans.filter(plan => plan.downPaymentRequired)
    if (downPayment && downPayment.length > 0) {
      return {
        siteFriendlyLabel: downPayment[0]?.siteFriendlyLabel,
        marketingMessage:
          downPayment[0]?.marketingMessage &&
          downPayment[0]?.marketingMessage?.markdown &&
          downPayment[0]?.marketingMessage?.markdown?.childMarkdownRemark &&
          downPayment[0]?.marketingMessage?.markdown?.childMarkdownRemark?.html,
      }
    }
    return (
      financePlans[0] && {
        siteFriendlyLabel: financePlans[0]?.siteFriendlyLabel,
        marketingMessage:
          financePlans[0]?.marketingMessage &&
          financePlans[0]?.marketingMessage?.markdown &&
          financePlans[0]?.marketingMessage?.markdown?.childMarkdownRemark &&
          financePlans[0]?.marketingMessage?.markdown?.childMarkdownRemark?.html,
      }
    )
  }
  return {
    siteFriendlyLabel: '',
    marketingMessage: '',
  }
}

export const getFinancePlansByOrder = order => {
  let financePlans = []
  if (financePlanFeed) {
    const loc = order.Division
    if (loc) {
      financePlans = financePlanFeed[`${loc.toLowerCase()}0}`] || financePlanFeed.fl0
    } else {
      financePlans = financePlanFeed.fl0
    }
  }
  return financePlans
}

export const displayKlarnaPDPWidget = ({ delivery_type, showFinance, price }) =>
  delivery_type === 'O' && !showFinance && price > 34 && price < 1001

export const getAllFinancePlans = () => {
  if (financePlanFeed) {
    return Object.values(financePlanFeed)?.reduce((plans, item) => {
      if (Array.isArray(item)) {
        return [...plans, ...item]
      }
      return plans
    })
  }
  return []
}

export const getWellsFargoFinancePlanRegion = () => {
  const loc = getCurrentLocation()
  return loc ? `${loc.region.toLowerCase()}${loc.price_zone}` : 'se0'
}

/**
 * Checks if the current order has a storeCartId saved in local storage.
 *
 * @returns {boolean} True if the order has a storeCartId, false otherwise.
 */
export const getLocalStoreCartId = () => {
  const order = getFromBrowserStorage('session', 'order')
  return order?.storeCart?.storeCartId
}

export const getSDPEncryption = async () => {
  if (getLocalStoreCartId()) {
    const sdpKey = await fetchEncryptedSDP()

    return sdpKey?.edp ? `&SDP=${sdpKey.edp}` : ''
  }
  return ''
}

/**
 * Returns the synchrony link based on the current order.
 * If the order has a storeCartId, the link is the synchrony store cart URL with the
 * '0249=UOM' query parameter. Otherwise, the link is the synchrony URL.
 *
 * @returns {string} The synchrony link.
 */
export const getSynchronyLink = () => {
  if (getLocalStoreCartId()) {
    return process.env.GATSBY_SYNCHRONY_STORE_CART_URL
  }
  return process.env.GATSBY_SYNCHRONY_URL
}

/**
 * Returns the merchant ID for the current order.
 *
 * @returns {string} The merchant ID, either for store cart or regular credit.
 */
export const getMerchantID = () =>
  // if (getLocalStoreCartId()) return process.env.GATSBY_STORE_CART_CREDIT_MID
  process.env.GATSBY_CREDIT_MID
